import { Graphics, type Sprite } from 'pixi.js';
import { clamp01, lerp, smoothstep } from '../../shared/scalar';
import { BURN } from '../../sim/full/attacks/cycle';
import type { PropState, SimEvent, SimState } from '../../sim/state';
import type { Family, RenderContext } from '../context';
import type { Frame } from '../frame';
import { type Shape, type Sheet, TAU, paintSheet, paintedLater } from '../paint';
import { add, byId, hide, setTint } from '../util';
import { ViewPool } from '../views';
import { ELEMENT_COLORS } from './avatar-paint';
import { MARK_PIECES, TONGUE } from './avatar-marks-paint';

type MarkShapes = Readonly<Record<keyof typeof MARK_PIECES, Shape>>;

const [FIRE_COLOR, WATER_COLOR] = ELEMENT_COLORS;
const SLOW_RIM = 0x8fd0ff;
const FROZEN = 0x4d3d2c;
const OUT = 0x040612;
const DENSITY = 2;
// By day, a dark line under what is pale enough to vanish on the floor.
const UNDER = { width: 1.6, alpha: 0.4 } as const;

const BURNING = {
  tongues: [
    [-0.42, 6, 0],
    [0.42, 7.5, 2],
  ],
  width: 1.35,
  // The bad vibe the flames were drawn for; a larger one burns with larger flames.
  radius: 9,
  rise: 0.7,
  grow: 0.12,
  fade: 0.3,
  glow: 1.8,
  glowAlpha: 0.22,
} as const;
const CHEVRON = { below: 6, half: 4, rise: 2, gap: 5, width: 1.8 } as const;
const PINS = { count: 3, gap: 4, landBeats: 0.18, sinkBeats: 0.25, size: 1 } as const;
const STAINS = { slowed: 0.28, frozen: 0.3 } as const;

interface BurnView {
  readonly glow: Sprite;
  readonly tongues: readonly [Sprite, Sprite];
}

export function createAvatarMarks(ctx: RenderContext): Family {
  const { layers, textures } = ctx;
  const avatars = new Set<string>();
  for (const [classId, attack] of ctx.attacks) {
    if (attack.kind === 'cycle') {
      avatars.add(classId);
    }
  }
  const sheet = paintedLater((): Sheet<keyof typeof MARK_PIECES> =>
    paintSheet(MARK_PIECES, DENSITY),
  );
  const stains = new Graphics();
  layers.zones.addChild(stains);
  const frozenSince = new Map<number, number>();
  const burnSince = new Map<number, number>();
  const slowed = new Set<number>();
  let lastHit: number | undefined;
  let burnViews: ViewPool<BurnView> | undefined;
  let pinViews: ViewPool<readonly Sprite[]> | undefined;

  function burnPool(shapes: MarkShapes): ViewPool<BurnView> {
    burnViews ??= new ViewPool<BurnView>(
      () => ({
        glow: add(layers.glow, textures.halo),
        tongues: [add(layers.weapons, shapes.tongue, 0), add(layers.weapons, shapes.tongue, 0)],
      }),
      (view) => {
        hide(view.glow, ...view.tongues);
      },
    );
    return burnViews;
  }

  function pinPool(shapes: MarkShapes): ViewPool<readonly Sprite[]> {
    pinViews ??= new ViewPool<readonly Sprite[]>(
      () => Array.from({ length: PINS.count }, () => add(layers.weapons, shapes.rock)),
      (rocks) => {
        hide(...rocks);
      },
    );
    return pinViews;
  }

  function under(frame: Frame, width: number): void {
    if (!frame.light.additive) {
      stains.stroke({ width: width + UNDER.width, color: OUT, alpha: UNDER.alpha, cap: 'round' });
    }
  }

  function drawBurn(state: SimState, burn: PropState, view: BurnView, alpha: number, frame: Frame) {
    const enemy = burn.targetId === undefined ? undefined : byId(state.enemies, burn.targetId);
    if (enemy === undefined) {
      hide(view.glow, ...view.tongues);
      return;
    }
    const { calm, light, tempo } = frame;
    const beat = tempo.ticksPerBeat;
    const since = burnSince.get(burn.id) ?? frame.now;
    burnSince.set(burn.id, since);
    const age = (frame.now - since) / beat;
    const grow =
      smoothstep(clamp01(age / BURNING.grow)) *
      smoothstep(clamp01(burn.ticksLeft / (BURNING.fade * beat)));
    const r = enemy.radius;
    const x = lerp(enemy.prevX, enemy.x, alpha);
    const y = lerp(enemy.prevY, enemy.y, alpha);
    view.glow.visible = true;
    view.glow.position.set(x, y);
    view.glow.scale.set((r * BURNING.glow) / textures.halo.radius);
    view.glow.alpha = BURNING.glowAlpha * grow * light.haloAlpha;
    setTint(view.glow, FIRE_COLOR);
    const beats = frame.now / beat;
    for (let index = 0; index < BURNING.tongues.length; index += 1) {
      const tongue = view.tongues[index];
      const [across, length, phase] = BURNING.tongues[index] ?? BURNING.tongues[0];
      if (tongue === undefined) {
        continue;
      }
      const flicker = calm ? 1 : 0.85 + 0.15 * Math.sin(beats * TAU * 1.2 + phase);
      tongue.visible = grow > 0;
      tongue.position.set(x + across * r, y - r * BURNING.rise);
      tongue.rotation = -Math.PI / 2;
      const size = (r / BURNING.radius) * grow;
      tongue.scale.set(
        (length * flicker * size) / TONGUE,
        (BURNING.width * length * size) / TONGUE,
      );
    }
  }

  function drawSlowed(state: SimState, alpha: number, frame: Frame): void {
    for (const id of slowed) {
      const enemy = byId(state.enemies, id);
      if (enemy === undefined || enemy.hp <= 0 || enemy.statuses?.slowed === undefined) {
        slowed.delete(id);
        continue;
      }
      const x = lerp(enemy.prevX, enemy.x, alpha);
      const cy = lerp(enemy.prevY, enemy.y, alpha);
      stains
        .circle(x, cy, enemy.radius)
        .fill({ color: WATER_COLOR, alpha: STAINS.slowed })
        .stroke({ width: 1.2, color: SLOW_RIM, alpha: 0.8 });
      const bob = frame.calm ? 0 : frame.pulse;
      const y = cy + enemy.radius + CHEVRON.below + bob;
      for (let index = 0; index < 2; index += 1) {
        const at = y + index * CHEVRON.gap;
        stains
          .moveTo(x - CHEVRON.half, at - CHEVRON.rise)
          .lineTo(x, at + CHEVRON.rise)
          .lineTo(x + CHEVRON.half, at - CHEVRON.rise);
        under(frame, CHEVRON.width);
        stains
          .moveTo(x - CHEVRON.half, at - CHEVRON.rise)
          .lineTo(x, at + CHEVRON.rise)
          .lineTo(x + CHEVRON.half, at - CHEVRON.rise)
          .stroke({ width: CHEVRON.width, color: WATER_COLOR, cap: 'round', join: 'round' });
      }
    }
  }

  function drawFrozen(
    state: SimState,
    alpha: number,
    pins: ViewPool<readonly Sprite[]>,
    frame: Frame,
  ) {
    const beat = frame.tempo.ticksPerBeat;
    for (const [id, since] of frozenSince) {
      const enemy = byId(state.enemies, id);
      const left = enemy?.statuses?.frozen;
      if (enemy === undefined || enemy.hp <= 0 || left === undefined) {
        frozenSince.delete(id);
        continue;
      }
      const x = lerp(enemy.prevX, enemy.x, alpha);
      const y = lerp(enemy.prevY, enemy.y, alpha);
      stains.circle(x, y, enemy.radius).fill({ color: FROZEN, alpha: STAINS.frozen });
      const age = (frame.now - since) / beat;
      const land = smoothstep(clamp01(age / PINS.landBeats));
      const sink = smoothstep(clamp01(left / (PINS.sinkBeats * beat)));
      const away = enemy.radius + PINS.gap;
      const rocks = pins.acquire(id);
      for (let index = 0; index < rocks.length; index += 1) {
        const rock = rocks[index];
        if (rock === undefined) {
          continue;
        }
        const angle = (index * TAU) / PINS.count + 0.9 + 0.25 * Math.sin(id * 7.3 + index);
        rock.visible = true;
        rock.position.set(x + Math.cos(angle) * away, y + Math.sin(angle) * away);
        rock.rotation = id * 1.7 + index * 2.4;
        rock.scale.set(PINS.size * land * sink * (1.15 + 0.15 * Math.sin(index * 3.1 + id)));
      }
    }
  }

  return {
    onEvent(event: SimEvent, state: SimState, frame: Frame): void {
      if (avatars.size === 0) {
        return;
      }
      if (event.type === 'enemyHit') {
        const by = event.by === undefined ? undefined : byId(state.players, event.by);
        lastHit = by !== undefined && avatars.has(by.classId) ? event.id : undefined;
      } else if (event.type === 'enemyStatusStarted' && event.id === lastHit) {
        if (event.status === 'frozen') {
          frozenSince.set(event.id, frame.now);
        } else if (event.status === 'slowed') {
          slowed.add(event.id);
        }
      }
    },
    reset(): void {
      frozenSince.clear();
      burnSince.clear();
      slowed.clear();
      lastHit = undefined;
      stains.clear();
      burnViews?.releaseAll();
      pinViews?.releaseAll();
    },
    update(state: SimState, alpha: number, frame: Frame): void {
      if (avatars.size === 0) {
        return;
      }
      const { shapes } = sheet.get();
      const burns = burnPool(shapes);
      const pins = pinPool(shapes);
      stains.clear();
      burns.begin();
      pins.begin();
      for (const prop of state.props ?? []) {
        if (prop.kind === BURN) {
          drawBurn(state, prop, burns.acquire(prop.id), alpha, frame);
        }
      }
      drawSlowed(state, alpha, frame);
      drawFrozen(state, alpha, pins, frame);
      burns.end();
      pins.end();
      for (const id of burnSince.keys()) {
        if (burns.peek(id) === undefined) {
          burnSince.delete(id);
        }
      }
    },
    destroy(): void {
      stains.destroy();
      sheet.painted?.source.destroy();
    },
  };
}
